import Icon from './Icon';
import { isExternalVendor, type Profiles } from '../people';

/**
 * THE VENDOR NAME-TAG — a small "Vendor" chip beside the name of anyone whose
 * workspace company role is External / Vendor (Feature B, B-07). It renders the
 * SAME on every surface a name appears — card faces, the Responsible/Accountable
 * pickers, the People & access roster, @-mention tokens, update and comment
 * attribution, the My Work and assignee lists — all from the one resolver
 * isExternalVendor, so a vendor can never be tagged in one place and unmarked in
 * another. It is driven by WHO they are (their company role), not by what they can
 * do on any one board: a supplier reads as a vendor even where they hold a fuller
 * access role.
 *
 * It reuses the app's brand-tint chip (the Received-tag / company-role family) and
 * encodes in FORM as well as colour — a building glyph, so it is never colour alone
 * (R28). Three densities, one meaning: the default tag; `compact`, tighter so a
 * picker or mention row keeps its height while still carrying the word; and
 * `glyphOnly`, the building on its own, for the very tightest place — a board tile
 * foot at its narrowest, where the line must never wrap (R23).
 */
export default function VendorTag({
  who,
  profiles,
  compact = false,
  glyphOnly = false,
}: {
  who: string;
  profiles: Profiles;
  compact?: boolean;
  glyphOnly?: boolean;
}) {
  if (!isExternalVendor(who, profiles)) return null;
  return (
    <span
      className={`vendor-tag${compact ? ' is-compact' : ''}${glyphOnly ? ' is-glyph' : ''}`}
      title="External vendor"
      aria-label="External vendor"
    >
      <Icon name="building" size={11} className="vendor-tag-glyph" />
      {!glyphOnly && <span className="vendor-tag-word">Vendor</span>}
    </span>
  );
}
